import { useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { createCalibrationFlow } from '../../interaction/calibration';
import { CalibrationTrainer } from './CalibrationTrainer';
import { evaluateReadiness } from '../../interaction/camera';
import { HERO_1_PLAYER_ID } from '../../interaction/keyboard';
import {
  CALIBRATION_STEP_LABELS,
  INVENTED_CALIBRATION_COPY,
} from '../../platform/config/calibration';
import { useAppServices } from '../shell/services-context';
import { stampCameraDeviceId } from '../../interaction/calibration';

/**
 * Hero training (CAM-02, GDS §13) — React-owned, like the readiness screen, because this is shell
 * work rather than gameplay (architecture §6).
 *
 * What the screen is allowed to know stays thin. It renders a `CalibrationFlowState` — statuses,
 * feedback codes, a visibility number — and hands the flow the session's frame feed without ever
 * naming a frame or a landmark itself (architecture §11, §25). The video element is the only place
 * a picture is visible, and it is mirrored, so the child sees themselves the way a mirror shows
 * them (MVP notes §5, §16).
 *
 * The framing is GDS §13's: "calibration should feel like hero training". So the child is asked to
 * show off moves, never to calibrate anything, and every step can be skipped except the two that
 * everything else is measured against — a child who cannot jump today still finishes training with
 * a profile that honestly says so.
 *
 * Copy discipline: every string comes from the config layer, and all of it is declared invented
 * (D-049(5)/D-061(4)) because GDS §13 contains no child-facing words at all.
 *
 * The play space is **followed, never chosen here** (Slice F): the chooser belongs to Parent Setup
 * (D-037(1)), and this screen reads what was chosen — or, on the first visit, what the camera check
 * recommended — so that step 6 measures the move the mission will actually ask for.
 *
 * **Not here, on purpose:** the parent's `RESTART_CALIBRATION` override (Slice G) and the
 * cooperative verification of §13 (Phase 4). Training ends on a done-for-now screen.
 */
export function CalibrationPage() {
  const { camera, tunables, activePlayer, setCameraInputEnabled, sessionLaunchConfig } =
    useAppServices();
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const playerId = activePlayer?.playerId ?? HERO_1_PLAYER_ID;

  /**
   * Which play space this training is for (D-059, D-063(1)).
   *
   * Precedence, and the order matters more than it looks: **a grown-up's explicit choice**, then
   * **what the camera check measured**, then the flagged default. Training normally happens before
   * Parent Setup on a first visit — camera check → hero training → games → setup — so without the
   * recommendation in the middle, the common path would measure a jump in a room that is about to
   * be set up as Compact, and architecture §20 would refuse the profile for `MODE_CHANGED`.
   */
  const cameraMode = sessionLaunchConfig?.cameraMode ?? camera.recommendedCameraMode ?? undefined;

  const flow = useMemo(
    () =>
      createCalibrationFlow({
        playerId,
        // What the child is looking at, for architecture §20's "viewport is similar" reuse rule.
        viewport: { width: window.innerWidth, height: window.innerHeight },
        frames: camera.poseFrames,
        cameraMode,
      }),
    [camera, playerId, cameraMode],
  );

  const state = useSyncExternalStore(
    flow.subscribe,
    () => flow.state,
    () => flow.state,
  );
  const snapshot = useSyncExternalStore(camera.subscribe, camera.getSnapshot, camera.getSnapshot);

  useEffect(() => {
    const gameplayCadenceMs = Math.round(1_000 / tunables.get().pose.poseTargetFps);

    camera.attach(videoRef.current);
    // Training needs gameplay cadence, not checklist cadence: the top of a jump is brief.
    camera.setSampleIntervalMs(gameplayCadenceMs);
    void camera.start();
    flow.attach();
    flow.start();

    // Leaving ends the camera (architecture §23; PRV-02) and abandons a half-finished training —
    // half a set of measurements is not a calibration.
    return () => {
      flow.detach();
      flow.cancel();
      camera.setSampleIntervalMs(null);
      camera.stop();
      camera.attach(null);
    };
  }, [camera, flow, tunables]);

  useEffect(() => {
    // The finished profile belongs to the sitting, and to nothing that outlives it (GDS §13).
    if (state.status === 'complete' && state.profile !== null) {
      camera.calibration.save(stampCameraDeviceId(state.profile, camera));
    }
  }, [camera, state.status, state.profile]);

  const report = evaluateReadiness(snapshot.readings);
  // Body-position guidance, in the readiness screen's own words: standing too close or in the dark
  // is the same problem here as it is there, and inventing a second vocabulary for it would teach
  // a child two names for one thing.
  const guidance = report.ready || report.blocked ? null : report.headline;

  if (state.status === 'complete') {
    return (
      <section data-testid="calibration">
        <h1 data-testid="calibration-copy">{INVENTED_CALIBRATION_COPY.pageTitle}</h1>
        <div className="panel" data-testid="calibration-complete">
          <h2 data-testid="calibration-copy">{INVENTED_CALIBRATION_COPY.completeTitle}</h2>
          <p data-testid="calibration-copy">{INVENTED_CALIBRATION_COPY.completeBody}</p>

          <h3 data-testid="calibration-copy">{INVENTED_CALIBRATION_COPY.movesTitle}</h3>
          <ul className="calibration__steps" data-testid="calibration-summary">
            {state.steps
              .filter((step) => step.capability !== null)
              .map((step) => (
                <li key={step.id} data-testid="calibration-summary-step" data-step-id={step.id}>
                  <span>{CALIBRATION_STEP_LABELS[step.id]}</span>
                  <span data-testid="calibration-copy">
                    {step.status === 'captured'
                      ? INVENTED_CALIBRATION_COPY.moveLearned
                      : INVENTED_CALIBRATION_COPY.moveSkipped}
                  </span>
                </li>
              ))}
          </ul>

          <p>
            {/*
             * The mission handoff (Slice D).
             *
             * This is the one moment where a child has just proved the camera can see them, so it
             * is where the offer to play with their body lives until Parent Setup takes it over
             * (Slice F, D-059). It only *asks*: the stage checks this sitting's calibration before
             * it will believe a camera can answer anything, and the keyboard keeps working either
             * way (CAM-09). The route is the ordinary one — a grown-up still chooses the game and
             * the session's shape — so nothing here needs to know a game exists.
             */}
            <button
              type="button"
              className="button button--primary"
              onClick={() => {
                setCameraInputEnabled(true);
                void navigate('/catalogue');
              }}
            >
              <span data-testid="calibration-copy">{INVENTED_CALIBRATION_COPY.playWithCamera}</span>
            </button>{' '}
            <Link to="/" className="button">
              <span data-testid="calibration-copy">{INVENTED_CALIBRATION_COPY.done}</span>
            </Link>
          </p>
        </div>
      </section>
    );
  }

  return (
    <section data-testid="calibration">
      <h1 data-testid="calibration-copy">{INVENTED_CALIBRATION_COPY.pageTitle}</h1>
      <p className="lede" data-testid="calibration-copy">
        {INVENTED_CALIBRATION_COPY.pageLede}
      </p>

      <div className="camera-check__stage calibration__stage">
        <video
          ref={videoRef}
          className="camera-check__feed"
          data-testid="camera-feed"
          data-mirrored="true"
          autoPlay
          playsInline
          muted
        />
        {/* Placeholder geometry, not artwork: the lane guide and player ring of MVP notes §16.
            Final art arrives through the theme manifest in Phase 5 and is never named here. */}
        <div className="calibration__lane-guide" data-testid="calibration-lane-guide" />
        <div
          className="calibration__player-ring"
          data-testid="calibration-player-ring"
          data-visibility={state.visibility >= 0.6 ? 'clear' : 'searching'}
        />
      </div>

      {/*
       * The sequence itself is shared with the stage's `RESTART_CALIBRATION` override (Slice G):
       * one child-facing training, mounted in two places, so a prompt cannot be worded one way
       * here and another way over a paused mission.
       */}
      <CalibrationTrainer flow={flow} state={state} guidance={guidance}>
        {/*
          Stopping training goes Home rather than back to the readiness screen, and the difference
          is a privacy one: the readiness screen would immediately re-open the camera, so a child
          who asked to stop would watch the indicator light up again.
        */}
        <Link to="/" className="button">
          <span data-testid="calibration-copy">{INVENTED_CALIBRATION_COPY.stop}</span>
        </Link>
      </CalibrationTrainer>
    </section>
  );
}
